import React, { FC, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { useProductFilters } from './useProductFilters';
import { FilterSetting, ProductFilter } from '../../store/api';
import { AggregationEntry, Aggregations } from '../../../models/Aggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { useAggregationsQuery, UseAggregationsQueryParams } from './useAggregationsQuery';

const emptyAggregations: AggregationEntry = {
    buckets: [],
    more: false,
    quicksearchQuery: '',
    value: ''
};

/**
 * Context of the filter bar.
 */
type FilterContext = {
    /**
     * The filters currently applied by the user.
     */
    readonly productFilter: ProductFilter;

    /**
     * Aggregations fetched from the backend based on the product filter.
     */
    readonly aggregations: Aggregations;

    /**
     * Handles the state change of a specific filter. Triggers a backend request.
     */
    readonly changeFilter: (category: string, include: ReadonlyArray<string>, exclude: ReadonlyArray<string>) => void;

    /**
     * Resets all selected filters.
     */
    readonly resetAllFilters: () => void;

    /**
     * Flag indicating whether the aggregations are currently being fetched from the backend.
     */
    readonly isFetchingAggregations: boolean;

    /**
     * Category that indicates if a filter group is expanded
     */
    expandedFilterGroups: string[];

    /**
     * Handles expanded filter group updates
     */
    setExpandedFilterGroups: (next: string[]) => void;

    seeAllFilterEntries: string[];
    setSeeAllFilterEntries: (next: string[]) => void;

    /**
     * Aggregations quick search fetch function callback
     */
    readonly fetchAggregationsQuickSearch: FetchAggregationsQuickSearch;
};

const defaultFilterContext: FilterContext = {
    productFilter: {},
    aggregations: {},
    changeFilter: () => {},
    resetAllFilters: () => {},
    isFetchingAggregations: false,
    fetchAggregationsQuickSearch: async () => ({}),
    expandedFilterGroups: [],
    setExpandedFilterGroups: () => {},
    seeAllFilterEntries: [],
    setSeeAllFilterEntries: () => {}
};

const Context = React.createContext<FilterContext>(defaultFilterContext);

export type FilterContextProviderParams = {
    readonly fetchAggregationsQuickSearch: FetchAggregationsQuickSearch;
} & UseAggregationsQueryParams;

export const FilterContextProvider: FC<PropsWithChildren<FilterContextProviderParams>> = ({
    visibleFilterGroups,
    buildQueryKey,
    fetchAggregations,
    fetchAggregationsQuickSearch,
    children
}) => {
    const { productFilter, changeFilter, resetAllFilters } = useProductFilters();

    const [expandedFilterGroups, setExpandedFilterGroups] = useState<string[]>(Object.keys(productFilter));
    const [seeAllFilterEntries, setSeeAllFilterEntries] = useState<string[]>([]);

    const { data: aggregations, isFetching: isFetchingAggregations } = useAggregationsQuery({
        visibleFilterGroups: visibleFilterGroups,
        fetchAggregations,
        buildQueryKey,
        expandedFilterGroups,
        seeAllFilterEntries
    });

    const contextValue = useMemo(
        (): FilterContext => ({
            productFilter,
            aggregations,
            isFetchingAggregations,
            fetchAggregationsQuickSearch,
            changeFilter,
            resetAllFilters,
            expandedFilterGroups,
            setExpandedFilterGroups,
            seeAllFilterEntries,
            setSeeAllFilterEntries
        }),
        [
            productFilter,
            aggregations,
            isFetchingAggregations,
            fetchAggregationsQuickSearch,
            changeFilter,
            resetAllFilters,
            expandedFilterGroups,
            seeAllFilterEntries
        ]
    );

    if (!aggregations) {
        return null;
    }

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

type UseFilterContextForCategory = {
    readonly filter: FilterSetting;
    readonly aggregation: AggregationEntry;
    readonly changeFilter: (category: string, include: ReadonlyArray<string>, exclude: ReadonlyArray<string>) => void;
    readonly resetAllFilters: () => void;
};

export const useFilterContextForCategory = (category: string): UseFilterContextForCategory => {
    const ctx = useContext(Context);

    return {
        filter: ctx.productFilter[category] ?? {},
        aggregation: ctx.aggregations[category] ?? emptyAggregations,
        changeFilter: ctx.changeFilter,
        resetAllFilters: ctx.resetAllFilters
    };
};

export const useFilterContext = (): FilterContext => {
    return useContext(Context);
};
